/**
 * The Art of Story - Storytelling Style Profile Assessment
 *
 * This assessment helps users understand their natural storytelling style and skill level
 * across 7 key dimensions of storytelling capability. It establishes a baseline for
 * tracking growth over time through the dashboard.
 *
 * Design Philosophy:
 * - Scenario-based questions that ask what you usually do, not what you think you should do (answers are still self-report)
 * - 23 carefully crafted questions (8-10 minutes) to maximise insights while respecting user time
 * - Weighted scoring across multiple dimensions per question for efficiency
 * - Aligned with The Art of Story curriculum (Modules 1-8)
 * - DDD brand voice: warm, research-grounded, aspirational but realistic
 */

const StorytellingStyleProfileAssessment = {
  id: 'SSP-1',
  title: "Storytelling Style Profile",
  subtitle: "Discover your unique storytelling strengths and growth opportunities",
  duration: "8-10 minutes",
  questionCount: 23,

  introduction: {
    heading: "Understanding Your Storytelling DNA",
    content: `This assessment helps you understand how you naturally tell stories: your strengths, your patterns, and where you might grow.

There are no "right" or "wrong" answers. We're not testing you; we're discovering your unique storytelling style so you can build from where you are.

**How it works:**
- Read each scenario and choose the response that feels most like you
- Answer honestly about what you actually do, not what you think you should do
- Your results will give you a baseline to track your growth over time

The whole thing takes about 8-10 minutes. Ready?`,
  },

  dimensions: {
    storyAwareness: {
      name: "Story Awareness",
      description: "Your ability to recognise and understand the narratives that shape your identity and how you make meaning from experiences",
      icon: "🧠",
      module: 1,
      spectrums: [
        {
          low: "Unreflective",
          high: "Story-Conscious",
          lowDescription: "Stories happen to you without much reflection. You rarely think about the patterns in how you tell your experiences or what those stories say about your identity.",
          highDescription: "You're aware that the stories you tell are part of who you are. You notice patterns in how you tell your experiences and recognise that changing how you tell them can change how you see yourself."
        }
      ]
    },

    narrativeStructure: {
      name: "Narrative Structure",
      description: "Your ability to construct well-formed stories with a clear shape that listeners can follow and remember, including oscillating narratives that tell a fuller, more honest story",
      icon: "🏗️",
      module: 2,
      spectrums: [
        {
          low: "Unstructured",
          high: "Architecturally Sound",
          lowDescription: "Your stories tend to ramble or focus on one emotion (all struggle or all triumph). They lack clear structure, making them harder for others to follow or remember.",
          highDescription: "You construct stories with clear beginnings, middles, and ends. You understand oscillating narratives that include both challenge and growth, which make for a fuller, more honest story."
        }
      ]
    },

    emotionalCraft: {
      name: "Emotional Craft",
      description: "Your ability to move listeners emotionally through specific sensory details and eudaemonic storytelling rather than abstract labels",
      icon: "❤️",
      module: 5,
      spectrums: [
        {
          low: "Abstract",
          high: "Evocative",
          lowDescription: "You tell stories using abstract emotion labels ('I was sad,' 'It was stressful') without specific sensory details. Listeners understand intellectually but don't feel the experience with you.",
          highDescription: "You craft stories with specific sensory details that let listeners build the feeling themselves. You understand eudaemonic storytelling: moving hearts to create lasting meaning, not just momentary entertainment."
        }
      ]
    },

    contextAdaptation: {
      name: "Context Adaptation",
      description: "Your ability to adapt the same story for different audiences and contexts whilst maintaining authenticity",
      icon: "🎭",
      module: 4,
      spectrums: [
        {
          low: "One-Size-Fits-All",
          high: "Contextually Fluent",
          lowDescription: "You tell stories the same way regardless of context. The story you tell at a job interview sounds the same as the one you'd tell a close friend, which can feel either too formal or too intimate.",
          highDescription: "You adapt your stories for context: same core experience, different emphasis and detail based on the relationship, setting, and purpose. You understand that context shapes what to include and what to leave unsaid."
        }
      ]
    },

    vulnerabilityCalibration: {
      name: "Vulnerability Calibration",
      description: "Your ability to share authentically with appropriate boundaries, matching vulnerability to relationship depth",
      icon: "🎚️",
      module: 6,
      spectrums: [
        {
          low: "Miscalibrated",
          high: "Appropriately Vulnerable",
          lowDescription: "You either overshare too soon (creating discomfort) or never share authentically (keeping people at a distance). You haven't calibrated vulnerability to relationship depth.",
          highDescription: "You understand this course's 10% Rule of thumb: sharing slightly beyond your current level of closeness to build connection. You know the difference between shame-based and growth-oriented vulnerable stories, and you match depth to context."
        }
      ]
    },

    deliveryPresence: {
      name: "Delivery Presence",
      description: "Your awareness of how you physically tell stories: voice, body language, eye contact, and managing nervous energy",
      icon: "🎤",
      module: 7,
      spectrums: [
        {
          low: "Self-Conscious",
          high: "Embodied",
          lowDescription: "You're either unaware of your physical presence when telling stories, or you're so self-conscious about it that it creates a barrier between you and your listeners. Nervous habits distract from your message.",
          highDescription: "You're aware of your voice, body language, and presence without being paralysed by self-consciousness. You notice nervous habits with self-compassion and can adjust. Your physical presence supports your story rather than distracting from it."
        }
      ]
    },

    storyListening: {
      name: "Story Listening",
      description: "Your ability to elicit and receive others' stories with empathy, without fixing or redirecting to your own experience",
      icon: "👂",
      module: 8,
      spectrums: [
        {
          low: "Self-Focussed",
          high: "Empathically Present",
          lowDescription: "When others tell stories, you redirect to your own experiences, offer unsolicited fixes, or listen while planning your response. You struggle to stay present with another person's narrative.",
          highDescription: "You can elicit stories through genuine curiosity, receive them without redirecting to your own experience, and resist the urge to fix or advise unless asked. You understand that listening is a gift that allows others to process meaning."
        }
      ]
    }
  },

  questions: [
    // STORY AWARENESS CLUSTER (Questions 1-3)
    {
      id: 1,
      type: "scenario",
      text: "You're reflecting on a challenging period from a few years ago. When you think about that time now, what's your natural tendency?",
      options: [
        {
          text: "I don't really think about it. It happened, and I've moved on",
          weights: {
            storyAwareness: -2,
            narrativeStructure: -1,
            emotionalCraft: -1
          }
        },
        {
          text: "I notice I've told that story differently over time as I've understood it better",
          weights: {
            storyAwareness: 2,
            narrativeStructure: 1,
            emotionalCraft: 1
          }
        },
        {
          text: "I tell it the same way I always have; the facts haven't changed",
          weights: {
            storyAwareness: -1,
            contextAdaptation: -1
          }
        },
        {
          text: "I've consciously reshaped how I tell it because the old version didn't serve me",
          weights: {
            storyAwareness: 2,
            narrativeStructure: 2,
            vulnerabilityCalibration: 1
          }
        }
      ]
    },

    {
      id: 2,
      type: "pattern",
      text: "When you tell stories about your past, which pattern sounds most like you?",
      options: [
        {
          text: "My stories tend to focus on what went wrong or how hard things were",
          weights: {
            narrativeStructure: -2,
            emotionalCraft: -1,
            storyAwareness: -1
          }
        },
        {
          text: "I mostly share victories and positive moments. No point dwelling on struggle",
          weights: {
            narrativeStructure: -1,
            vulnerabilityCalibration: -2,
            storyAwareness: -1
          }
        },
        {
          text: "I include both struggle and growth: the hard parts and what I learnt from them",
          weights: {
            narrativeStructure: 2,
            storyAwareness: 2,
            emotionalCraft: 1,
            vulnerabilityCalibration: 1
          }
        },
        {
          text: "I don't really notice patterns in how I tell my stories",
          weights: {
            storyAwareness: -2,
            narrativeStructure: -1
          }
        }
      ]
    },

    {
      id: 3,
      type: "scenario",
      text: "A friend tells you about a recent setback. Your first instinct is to share a similar experience from your own life. What do you do?",
      options: [
        {
          text: "Share my story immediately. Showing I relate is how I support people",
          weights: {
            storyListening: -2,
            storyAwareness: -1
          }
        },
        {
          text: "Pause and ask a question about their experience first, then maybe share mine if relevant",
          weights: {
            storyListening: 2,
            storyAwareness: 1,
            vulnerabilityCalibration: 1
          }
        },
        {
          text: "Share my story but keep it brief so I can get back to theirs",
          weights: {
            storyListening: 0,
            storyAwareness: 1
          }
        },
        {
          text: "Focus entirely on their experience; they need space, not my story",
          weights: {
            storyListening: 2,
            emotionalCraft: 1
          }
        }
      ]
    },

    // NARRATIVE STRUCTURE CLUSTER (Questions 4-6)
    {
      id: 4,
      type: "scenario",
      text: "You're telling someone about a time you failed at something that mattered to you. How does your story typically unfold?",
      options: [
        {
          text: "I share what happened and how bad I felt, and the story ends with the failure",
          weights: {
            narrativeStructure: -2,
            emotionalCraft: -1,
            storyAwareness: -1
          }
        },
        {
          text: "I jump straight to the lesson or silver lining. No point dwelling on the hard part",
          weights: {
            narrativeStructure: -1,
            vulnerabilityCalibration: -2,
            emotionalCraft: -1
          }
        },
        {
          text: "I tell the whole arc, struggle and growth: what I tried, what went wrong, how I felt, and what I learnt",
          weights: {
            narrativeStructure: 2,
            emotionalCraft: 2,
            vulnerabilityCalibration: 1,
            storyAwareness: 1
          }
        },
        {
          text: "I tend to ramble through the details without a clear ending",
          weights: {
            narrativeStructure: -2,
            deliveryPresence: -1
          }
        }
      ]
    },

    {
      id: 5,
      type: "scenario",
      text: "Someone asks you to tell them about yourself. How do you typically respond?",
      options: [
        {
          text: "I list facts: where I'm from, what I do, maybe some hobbies",
          weights: {
            narrativeStructure: -2,
            emotionalCraft: -2,
            storyAwareness: -1
          }
        },
        {
          text: "I tell a short story that captures something important about who I am",
          weights: {
            narrativeStructure: 2,
            contextAdaptation: 1,
            storyAwareness: 2,
            emotionalCraft: 1
          }
        },
        {
          text: "I share a lot of different fragments without a clear through-line",
          weights: {
            narrativeStructure: -1,
            deliveryPresence: -1
          }
        },
        {
          text: "I deflect or keep it really surface. I'm not comfortable talking about myself",
          weights: {
            vulnerabilityCalibration: -2,
            storyAwareness: -1,
            narrativeStructure: -1
          }
        }
      ]
    },

    {
      id: 6,
      type: "pattern",
      text: "When you tell a story, how do listeners typically respond?",
      options: [
        {
          text: "They seem engaged but sometimes lose the thread or ask 'wait, what happened?'",
          weights: {
            narrativeStructure: -1,
            deliveryPresence: -1,
            emotionalCraft: 0
          }
        },
        {
          text: "They lean in, ask follow-up questions, and remember the story later",
          weights: {
            narrativeStructure: 2,
            emotionalCraft: 2,
            deliveryPresence: 1,
            contextAdaptation: 1
          }
        },
        {
          text: "They're polite but I can see their attention drifting",
          weights: {
            narrativeStructure: -2,
            emotionalCraft: -2,
            deliveryPresence: -1
          }
        },
        {
          text: "I honestly don't pay much attention to how people respond",
          weights: {
            storyAwareness: -2,
            storyListening: -1,
            deliveryPresence: -2
          }
        }
      ]
    },

    // EMOTIONAL CRAFT CLUSTER (Questions 7-9)
    {
      id: 7,
      type: "scenario",
      text: "You're describing a moment when you felt deeply moved (joy, grief, awe, whatever the emotion). How do you tend to tell it?",
      options: [
        {
          text: "I name the emotion ('I was so happy' or 'I felt devastated') and explain why",
          weights: {
            emotionalCraft: -2,
            narrativeStructure: -1
          }
        },
        {
          text: "I describe what I saw, heard, felt physically, so the listener experiences it with me",
          weights: {
            emotionalCraft: 2,
            narrativeStructure: 1,
            deliveryPresence: 1
          }
        },
        {
          text: "I keep it factual: what happened, not how I felt",
          weights: {
            emotionalCraft: -2,
            vulnerabilityCalibration: -2,
            storyAwareness: -1
          }
        },
        {
          text: "I share the emotion but get self-conscious and cut it short",
          weights: {
            emotionalCraft: -1,
            deliveryPresence: -2,
            vulnerabilityCalibration: -1
          }
        }
      ]
    },

    {
      id: 8,
      type: "scenario",
      text: "You're telling a story and you notice the other person looks confused or distracted. What do you do?",
      options: [
        {
          text: "Keep going. Maybe they'll catch up, or they can ask if they're lost",
          weights: {
            deliveryPresence: -2,
            storyListening: -1,
            emotionalCraft: -1
          }
        },
        {
          text: "Pause and check in: 'Am I making sense?' or 'Should I back up?'",
          weights: {
            deliveryPresence: 2,
            storyListening: 1,
            contextAdaptation: 1
          }
        },
        {
          text: "Get flustered and rush through to the end",
          weights: {
            deliveryPresence: -2,
            emotionalCraft: -1,
            narrativeStructure: -1
          }
        },
        {
          text: "I usually don't notice how people are responding whilst I'm telling a story",
          weights: {
            deliveryPresence: -2,
            storyListening: -2,
            storyAwareness: -1
          }
        }
      ]
    },

    {
      id: 9,
      type: "pattern",
      text: "After you've shared a meaningful story with someone, what tends to happen?",
      options: [
        {
          text: "They remember it and reference it later, sometimes in ways that surprise me",
          weights: {
            emotionalCraft: 2,
            narrativeStructure: 2,
            vulnerabilityCalibration: 1
          }
        },
        {
          text: "They might remember the topic, but not the specific story or details",
          weights: {
            emotionalCraft: -1,
            narrativeStructure: -1
          }
        },
        {
          text: "People tend to forget I told them, so I end up repeating myself",
          weights: {
            emotionalCraft: -2,
            narrativeStructure: -2,
            deliveryPresence: -1
          }
        },
        {
          text: "They share a meaningful story back, and the conversation deepens",
          weights: {
            emotionalCraft: 2,
            storyListening: 2,
            vulnerabilityCalibration: 2,
            narrativeStructure: 1
          }
        }
      ]
    },

    // CONTEXT ADAPTATION CLUSTER (Questions 10-12)
    {
      id: 10,
      type: "scenario",
      text: "You have a story about overcoming a professional challenge. You're asked about it in two different contexts: a job interview and a dinner with close friends. How do you tell it?",
      options: [
        {
          text: "Pretty much the same way. It's the same story, same facts",
          weights: {
            contextAdaptation: -2,
            vulnerabilityCalibration: -1,
            storyAwareness: -1
          }
        },
        {
          text: "Very differently: same core experience, but I emphasise different parts and adjust the depth",
          weights: {
            contextAdaptation: 2,
            vulnerabilityCalibration: 2,
            narrativeStructure: 1,
            storyAwareness: 1
          }
        },
        {
          text: "I avoid the story with friends; it feels too 'professional' for that setting",
          weights: {
            contextAdaptation: -1,
            storyAwareness: -1
          }
        },
        {
          text: "I don't really think about it. I just tell it how it comes out",
          weights: {
            contextAdaptation: -2,
            storyAwareness: -2
          }
        }
      ]
    },

    {
      id: 11,
      type: "scenario",
      text: "You're at a work event with colleagues you don't know well. Someone asks about your weekend. You had a tough family conversation. What do you share?",
      options: [
        {
          text: "The whole thing. Authenticity matters, and I don't want to be fake",
          weights: {
            contextAdaptation: -2,
            vulnerabilityCalibration: -2
          }
        },
        {
          text: "Something vague and positive ('It was good, saw family') and redirect the conversation",
          weights: {
            contextAdaptation: 0,
            vulnerabilityCalibration: -1,
            storyListening: 0
          }
        },
        {
          text: "A true but boundaried version: 'Had some family stuff to navigate, but it's sorting itself out. How was yours?'",
          weights: {
            contextAdaptation: 2,
            vulnerabilityCalibration: 2,
            storyAwareness: 1,
            storyListening: 1
          }
        },
        {
          text: "Nothing real. I'll make up a story about something else I did",
          weights: {
            vulnerabilityCalibration: -2,
            contextAdaptation: -1,
            storyAwareness: -1
          }
        }
      ]
    },

    {
      id: 12,
      type: "pattern",
      text: "When you tell the same story to different people, what's most true for you?",
      options: [
        {
          text: "I don't adjust much. The story is the story",
          weights: {
            contextAdaptation: -2,
            storyAwareness: -1
          }
        },
        {
          text: "I adjust naturally without thinking about it; something just feels different in different contexts",
          weights: {
            contextAdaptation: 1,
            storyAwareness: 0
          }
        },
        {
          text: "I consciously adapt based on the relationship, setting, and what would be meaningful to that person",
          weights: {
            contextAdaptation: 2,
            storyAwareness: 2,
            vulnerabilityCalibration: 1,
            emotionalCraft: 1
          }
        },
        {
          text: "I avoid telling the same story twice; it feels repetitive",
          weights: {
            contextAdaptation: -1,
            narrativeStructure: -1
          }
        }
      ]
    },

    // VULNERABILITY CALIBRATION CLUSTER (Questions 13-15)
    {
      id: 13,
      type: "scenario",
      text: "You're on a first date. The conversation is flowing, and you're asked about a past relationship. What feels most like you?",
      options: [
        {
          text: "Share the whole story, including the painful parts. Vulnerability builds connection",
          weights: {
            vulnerabilityCalibration: -2,
            contextAdaptation: -2
          }
        },
        {
          text: "Keep it vague and positive. It's too early for that level of depth",
          weights: {
            vulnerabilityCalibration: -1,
            contextAdaptation: 0
          }
        },
        {
          text: "Share something real but boundaried: true, without oversharing for this stage of connection",
          weights: {
            vulnerabilityCalibration: 2,
            contextAdaptation: 2,
            storyAwareness: 1
          }
        },
        {
          text: "Deflect with humour or change the subject. I don't share that kind of thing easily",
          weights: {
            vulnerabilityCalibration: -2,
            emotionalCraft: -1
          }
        }
      ]
    },

    {
      id: 14,
      type: "pattern",
      text: "After you've shared something vulnerable, how do you typically feel?",
      options: [
        {
          text: "Regretful. I replay it and wish I'd said less",
          weights: {
            vulnerabilityCalibration: -2,
            storyAwareness: -1,
            deliveryPresence: -1
          }
        },
        {
          text: "Good if the person responded well, anxious if they didn't",
          weights: {
            vulnerabilityCalibration: 0,
            storyAwareness: 0,
            deliveryPresence: -1
          }
        },
        {
          text: "Grounded. I shared what felt right for that relationship at that moment",
          weights: {
            vulnerabilityCalibration: 2,
            storyAwareness: 2,
            contextAdaptation: 1,
            deliveryPresence: 1
          }
        },
        {
          text: "I don't really share vulnerably, so this doesn't come up for me",
          weights: {
            vulnerabilityCalibration: -2,
            emotionalCraft: -2,
            storyAwareness: -1
          }
        }
      ]
    },

    {
      id: 15,
      type: "scenario",
      text: "You're telling a story about a time you made a significant mistake. How do you tend to frame it?",
      options: [
        {
          text: "I focus on how ashamed I felt and really emphasise how bad I felt about myself",
          weights: {
            vulnerabilityCalibration: -2,
            narrativeStructure: -1,
            emotionalCraft: -1
          }
        },
        {
          text: "I focus on what I did wrong and what I learnt: the behaviour, not my worth as a person",
          weights: {
            vulnerabilityCalibration: 2,
            narrativeStructure: 2,
            storyAwareness: 1,
            emotionalCraft: 1
          }
        },
        {
          text: "I downplay it or make it funny. I don't want to seem like I'm dwelling on it",
          weights: {
            vulnerabilityCalibration: -1,
            emotionalCraft: -1
          }
        },
        {
          text: "I don't tell stories about my mistakes. Too uncomfortable",
          weights: {
            vulnerabilityCalibration: -2,
            narrativeStructure: -1,
            storyAwareness: -1
          }
        }
      ]
    },

    // DELIVERY PRESENCE CLUSTER (Questions 16-18)
    {
      id: 16,
      type: "scenario",
      text: "You're telling a story to a small group. Halfway through, you notice your hands are fidgeting or you've said 'um' several times. What happens?",
      options: [
        {
          text: "I don't usually notice things like that whilst I'm speaking",
          weights: {
            deliveryPresence: -2,
            storyAwareness: -1
          }
        },
        {
          text: "I notice and get self-conscious, which makes it worse",
          weights: {
            deliveryPresence: -1,
            emotionalCraft: -1
          }
        },
        {
          text: "I notice with self-compassion, maybe adjust, and keep going without making it a big deal",
          weights: {
            deliveryPresence: 2,
            storyAwareness: 1,
            emotionalCraft: 1
          }
        },
        {
          text: "I notice and apologise or comment on it: 'Sorry, I'm nervous'",
          weights: {
            deliveryPresence: -1,
            vulnerabilityCalibration: 0
          }
        }
      ]
    },

    {
      id: 17,
      type: "pattern",
      text: "When you're telling an important story, what's happening with your body?",
      options: [
        {
          text: "I'm tense or holding myself rigidly, trying to control my nerves",
          weights: {
            deliveryPresence: -2,
            emotionalCraft: -1
          }
        },
        {
          text: "I move naturally; my hands, face, and body support what I'm saying",
          weights: {
            deliveryPresence: 2,
            emotionalCraft: 1,
            narrativeStructure: 1
          }
        },
        {
          text: "I'm not aware of my body. All my attention is on the words",
          weights: {
            deliveryPresence: -2,
            storyAwareness: -1
          }
        },
        {
          text: "I'm so aware of my body that it's distracting. I'm monitoring every gesture",
          weights: {
            deliveryPresence: -1,
            storyAwareness: 0
          }
        }
      ]
    },

    {
      id: 18,
      type: "scenario",
      text: "You've just finished telling a story you care about. The person's response doesn't match the energy you felt whilst telling it. What do you do?",
      options: [
        {
          text: "Feel deflated but don't say anything. Maybe I misjudged how interesting it was",
          weights: {
            deliveryPresence: -1,
            storyAwareness: -1,
            vulnerabilityCalibration: -1
          }
        },
        {
          text: "Get curious: 'Did that land? I felt like maybe I lost you'",
          weights: {
            deliveryPresence: 2,
            storyListening: 1,
            contextAdaptation: 1,
            storyAwareness: 1
          }
        },
        {
          text: "Fill the silence by explaining more or telling another story",
          weights: {
            deliveryPresence: -2,
            storyListening: -2,
            vulnerabilityCalibration: -1
          }
        },
        {
          text: "I don't usually notice mismatches in energy",
          weights: {
            deliveryPresence: -2,
            storyListening: -2,
            storyAwareness: -1
          }
        }
      ]
    },

    // STORY LISTENING CLUSTER (Questions 19-21)
    {
      id: 19,
      type: "scenario",
      text: "A colleague is telling you about a conflict at work. As they talk, you think of advice that might help. What do you do?",
      options: [
        {
          text: "Jump in with the advice. I want to be helpful",
          weights: {
            storyListening: -2,
            vulnerabilityCalibration: -1
          }
        },
        {
          text: "Wait for a pause, then offer the advice",
          weights: {
            storyListening: -1,
            contextAdaptation: 0
          }
        },
        {
          text: "Notice the urge to fix, stay present with their story, and only offer advice if they ask",
          weights: {
            storyListening: 2,
            emotionalCraft: 1,
            vulnerabilityCalibration: 1
          }
        },
        {
          text: "Listen whilst planning what I'll say when it's my turn",
          weights: {
            storyListening: -2,
            storyAwareness: -1
          }
        }
      ]
    },

    {
      id: 20,
      type: "pattern",
      text: "When someone is telling you a story, where is your attention?",
      options: [
        {
          text: "On how their story relates to my own experience",
          weights: {
            storyListening: -2,
            storyAwareness: -1
          }
        },
        {
          text: "On their words, face and emotion, trying to understand their experience from the inside",
          weights: {
            storyListening: 2,
            emotionalCraft: 1,
            deliveryPresence: 1
          }
        },
        {
          text: "Split: part listening, part thinking about other things",
          weights: {
            storyListening: -2,
            deliveryPresence: -1
          }
        },
        {
          text: "On what I'm going to say in response",
          weights: {
            storyListening: -2,
            storyAwareness: -1
          }
        }
      ]
    },

    {
      id: 21,
      type: "scenario",
      text: "Someone shares a difficult story with you. When they finish, they seem to be sitting with the emotion. What do you do?",
      options: [
        {
          text: "Jump in to make them feel better: offer reassurance or shift to something positive",
          weights: {
            storyListening: -2,
            vulnerabilityCalibration: -1,
            emotionalCraft: -1
          }
        },
        {
          text: "Sit with them in the silence and let the emotion be there without rushing to fix it",
          weights: {
            storyListening: 2,
            emotionalCraft: 2,
            vulnerabilityCalibration: 1,
            deliveryPresence: 1
          }
        },
        {
          text: "Share my own similar experience to show I understand",
          weights: {
            storyListening: -1,
            storyAwareness: 0
          }
        },
        {
          text: "Feel uncomfortable and look for a way to change the subject",
          weights: {
            storyListening: -2,
            vulnerabilityCalibration: -2,
            emotionalCraft: -1
          }
        }
      ]
    },

    // INTEGRATION & SYNTHESIS (Questions 22-23)
    {
      id: 22,
      type: "scenario",
      text: "You're preparing to tell a story that matters to you in a context that matters (job interview, important conversation, meaningful presentation). How do you prepare?",
      options: [
        {
          text: "I don't prepare. Authenticity means being spontaneous",
          weights: {
            narrativeStructure: -2,
            contextAdaptation: -1,
            deliveryPresence: -1
          }
        },
        {
          text: "I think through the key points and maybe practise it once or twice",
          weights: {
            narrativeStructure: 1,
            deliveryPresence: 1,
            contextAdaptation: 1
          }
        },
        {
          text: "I script it word-for-word and rehearse until I can deliver it perfectly",
          weights: {
            deliveryPresence: -1,
            emotionalCraft: -1,
            contextAdaptation: 0
          }
        },
        {
          text: "I think about my audience, what matters to them, the emotional arc I want to create, and practise with that intention",
          weights: {
            narrativeStructure: 2,
            emotionalCraft: 2,
            contextAdaptation: 2,
            deliveryPresence: 1,
            storyAwareness: 1
          }
        }
      ]
    },

    {
      id: 23,
      type: "reflection",
      text: "Thinking about your storytelling overall, which statement resonates most?",
      options: [
        {
          text: "I tell stories naturally but haven't thought much about how or why. It just happens",
          weights: {
            storyAwareness: -2,
            narrativeStructure: -1,
            contextAdaptation: -1
          }
        },
        {
          text: "I'm aware of some storytelling patterns but inconsistent: sometimes great, sometimes not",
          weights: {
            storyAwareness: 0,
            narrativeStructure: 0,
            contextAdaptation: 0,
            emotionalCraft: 0
          }
        },
        {
          text: "I'm intentional about storytelling and notice when it connects, though I know there's more to learn",
          weights: {
            storyAwareness: 2,
            narrativeStructure: 1,
            emotionalCraft: 1,
            contextAdaptation: 1,
            vulnerabilityCalibration: 1
          }
        },
        {
          text: "I avoid storytelling when I can. I'm more comfortable with facts and information",
          weights: {
            storyAwareness: -2,
            emotionalCraft: -2,
            vulnerabilityCalibration: -2,
            narrativeStructure: -1
          }
        }
      ]
    }
  ],

  // Profile types are generated based on combinations of dimension scores
  // These help users understand their unique storytelling DNA
  profileTypes: {

    emergent: {
      name: "The Emerging Storyteller",
      tagline: "You're building awareness, and there's so much possibility ahead",
      description: "You're at the beginning of your storytelling journey, and that's exactly where everyone starts. You might not have thought much about how you tell stories or the patterns in your narratives, but you're here, and that awareness itself is the first step. Your growth potential is enormous.",
      strengths: [
        "Openness to learning and growth",
        "Authentic presence without overthinking",
        "Fresh perspective unconstrained by habits"
      ],
      growthAreas: [
        "Developing awareness of narrative patterns",
        "Building structure into your stories",
        "Recognising how stories shape identity",
        "Calibrating vulnerability to context"
      ],
      nextSteps: [
        "Start with Module 1 (Your Story Landscape) to build awareness of how stories shape your life",
        "Notice patterns in stories you tell and stories you tell yourself",
        "Begin a simple story inventory: what experiences do you find yourself sharing?"
      ],
      moduleFocus: [1, 2, 3]
    },

    naturalConnector: {
      name: "The Natural Connector",
      tagline: "You connect through story instinctively. Now let's add structure",
      description: "You have a gift for emotional connection and genuine presence with others. People feel heard when they're with you, and you share authentically. Your stories create warmth and intimacy. The next level for you is adding intentional structure and context awareness to your natural relational gifts.",
      strengths: [
        "Emotional authenticity and warmth",
        "Strong listening and empathic presence",
        "Vulnerable in ways that build connection",
        "People feel safe with you"
      ],
      growthAreas: [
        "Adding structure to make stories more memorable",
        "Adapting storytelling for different contexts",
        "Using specific sensory details instead of emotion labels",
        "Building awareness of nervous habits"
      ],
      nextSteps: [
        "Module 3 (Stories That Hold Struggle and Strength) will help you tell hard experiences with both the struggle and the growth",
        "Module 4 (Story Structure That Works) will give your natural warmth more structure and help you adjust stories for different settings",
        "Practise with the Emotional Arc Mapper in the Toolkit to strengthen your already-strong connection"
      ],
      moduleFocus: [3, 4, 5]
    },

    analyticalNarrator: {
      name: "The Analytical Narrator",
      tagline: "You value clarity and structure. Let's add emotional resonance",
      description: "You're thoughtful about how you tell stories and aware of structure and context. You prepare well and adapt appropriately. Your challenge is moving from your head to your heart: letting listeners feel the experience, not just understand it intellectually. When you add emotional craft to your structural gifts, you'll be extraordinary.",
      strengths: [
        "Clear narrative structure",
        "Context awareness and adaptation",
        "Thoughtful preparation",
        "Good boundaries and calibration"
      ],
      growthAreas: [
        "Adding sensory details and emotional depth",
        "Moving from abstract to evocative language",
        "Allowing vulnerability without over-controlling",
        "Balancing preparation with spontaneity"
      ],
      nextSteps: [
        "Module 5 (Moving the Heart to Change the Mind) is likely where you'll grow most: it's all about emotional craft",
        "Practise describing moments with sensory details instead of emotion labels",
        "Module 6 (Vulnerability Without Oversharing) will help you share more of yourself whilst maintaining your thoughtful boundaries"
      ],
      moduleFocus: [5, 6, 7]
    },

    expressivePerformer: {
      name: "The Expressive Performer",
      tagline: "You captivate naturally. Let's add depth and calibration",
      description: "You light up when you tell stories. Your delivery is engaging, your emotional craft is strong, and people remember your stories. Your growth opportunity is in calibration: matching depth to context, listening as skilfully as you speak, and being intentional about what you share and why. When you add discernment to your natural expressiveness, you'll be masterful.",
      strengths: [
        "Engaging delivery and presence",
        "Strong emotional craft and evocative details",
        "Memorable stories that stick with people",
        "Comfortable being seen and heard"
      ],
      growthAreas: [
        "Calibrating vulnerability to relationship depth",
        "Adapting stories for professional vs. intimate contexts",
        "Strengthening listening skills",
        "Balancing expressiveness with structure"
      ],
      nextSteps: [
        "Module 6 (Vulnerability Without Oversharing) will help you match depth to context",
        "Module 8 (The Art of Story Listening) will round out your skills: you're great at telling, now master receiving",
        "Module 4 (Story Structure That Works) will help you adjust your natural expressiveness for different settings"
      ],
      moduleFocus: [4, 6, 8]
    },

    reflectiveBuilder: {
      name: "The Reflective Builder",
      tagline: "You're consciously developing your craft. Keep building",
      description: "You're aware of storytelling as a skill and you're intentional about developing it. You notice patterns, you think about structure, you adapt for context. You're in the middle of your journey: not at the beginning, but with room to grow. Your self-awareness is a huge asset. Keep building on this strong foundation.",
      strengths: [
        "High self-awareness about storytelling patterns",
        "Intentional about structure and context",
        "Balance of authenticity and thoughtfulness",
        "Commitment to growth and learning"
      ],
      growthAreas: [
        "Deepening emotional craft and sensory details",
        "Strengthening delivery presence",
        "Further calibrating vulnerability",
        "Enhancing story listening skills"
      ],
      nextSteps: [
        "You'll benefit from all modules, but especially Module 5 (Moving the Heart to Change the Mind) and Module 7 (Story Presence)",
        "Use the Toolkit regularly to practise and get feedback: you're the kind of person who thrives with feedback",
        "Rehearse your stories with Marlow, the app's story coach"
      ],
      moduleFocus: [5, 7, 8]
    },

    skilledStoryteller: {
      name: "The Skilled Storyteller",
      tagline: "You've developed real mastery. Now go deeper and wider",
      description: "You scored high across most dimensions. You're aware of storytelling as craft, you structure stories well, you adapt for context, you calibrate vulnerability, you listen empathically. You likely didn't need this assessment to know you're skilled, but there's always another level. For you, growth is about nuance, expanding your range, and teaching others.",
      strengths: [
        "Strong across all dimensions of storytelling",
        "High awareness and intentionality",
        "Skilled at both telling and listening",
        "Emotional depth combined with structural clarity"
      ],
      growthAreas: [
        "Mastering the most challenging contexts",
        "Teaching and mentoring others",
        "Exploring advanced techniques",
        "Continuing to deepen all dimensions"
      ],
      nextSteps: [
        "Work through all modules to deepen and refine",
        "Revisit Module 4 (Story Structure That Works) and Module 6 (Vulnerability Without Oversharing) for the hardest contexts: professional, intimate and challenging",
        "Rehearse your highest-stakes stories with Marlow, the app's story coach",
        "Consider how you might teach these skills to others"
      ],
      moduleFocus: [5, 6, 8]
    }
  },

  // Scoring algorithm to determine profile type
  // StorytellingStyleAssessment.jsx applies these same thresholds; there is no more detailed version
  getProfileType: (dimensionScores) => {
    // Calculate average score across all dimensions
    const avgScore = Object.values(dimensionScores).reduce((a, b) => a + b, 0) / 7;

    // Get individual dimension scores (normalised to 0-100 scale)
    const structure = dimensionScores.narrativeStructure;
    const emotional = dimensionScores.emotionalCraft;
    const context = dimensionScores.contextAdaptation;
    const vulnerability = dimensionScores.vulnerabilityCalibration;
    const delivery = dimensionScores.deliveryPresence;
    const listening = dimensionScores.storyListening;

    // Profile type logic
    if (avgScore >= 75) {
      return 'skilledStoryteller';
    }

    if (avgScore >= 55) {
      return 'reflectiveBuilder';
    }

    if (emotional >= 65 && delivery >= 65 && (vulnerability < 50 || listening < 50)) {
      return 'expressivePerformer';
    }

    if (structure >= 60 && context >= 60 && emotional < 50) {
      return 'analyticalNarrator';
    }

    if (listening >= 65 && vulnerability >= 60 && (structure < 50 || context < 50)) {
      return 'naturalConnector';
    }

    return 'emergent';
  },

  completionMessage: {
    heading: "Your Storytelling Style Profile is Ready",
    content: `We've mapped your unique storytelling DNA across 7 key dimensions.

This isn't a test you pass or fail. It's a baseline. A starting point for growth.

Whatever your results show, remember: storytelling is a learnable skill. Good storytellers develop their craft over time, with intention and practice.

You're in exactly the right place to do the same.

Let's see what you discovered...`
  }
};

export default StorytellingStyleProfileAssessment;
